import { WorkflowFormProvider } from "@/features/workflows/components/workflow-form-context";
import { WorkflowStepTag } from "@/features/workflows/components/workflow-step-tag";
import { WorkflowTransition } from "@/features/workflows/components/workflow-transition";
import { useHasCompanyPermission } from "@/shared/hooks/use-has-permission";
import { useGetMe } from "@/spz-api/user/user";
import { CompanyPermission } from "@spz/shared-domain";
import { StatusTag } from "@spz/ui/components/status-tag";
import { Typography } from "@spz/ui/components/typography";
import { useCallback, useMemo } from "react";
import { useChangeControlResultPriority } from "../../hooks/use-change-control-result-priority";
import { useUpdateControlResultOwner } from "../../hooks/use-update-control-result-owner";
import { useBulkActionStore } from "../../store/bulk-action.store";
import { filterWorkflowTransitions } from "../../utils/workflow-transitions-builder";
import { selectControlResultDetailsViewModel } from "../../view-models/select-control-result-details.view-model";
import { OwnershipSelector } from "../ownership-selector";
import { PrioritySelector } from "../priority-selector";
import ResultTitle from "./result-title";

interface ResultDetailSheetHeaderProps {
  controlId: string;
  titleControlResultId: string;
  descriptorName: string;
  companyId: string;
  resultId: string;
  onLoadError: () => void;
}

export const ResultDetailSheetHeader: React.FC<
  ResultDetailSheetHeaderProps
> = ({
  controlId,
  titleControlResultId,
  descriptorName,
  companyId,
  resultId,
  onLoadError,
}) => {
  const { data: user } = useGetMe();

  const { workflow, rootCause, owner, handleTransitionSubmit, priority } =
    selectControlResultDetailsViewModel({
      controlId,
      controlResultId: resultId,
      onLoadError,
    });
  const { isBulkActionInProgress } = useBulkActionStore();

  const { mutate } = useUpdateControlResultOwner(controlId, companyId);
  const { handlePriorityChange } = useChangeControlResultPriority(
    controlId,
    resultId,
  );

  const handleOwnershipChange = useCallback(
    (ownerId: string | null, controlResultId: string) => {
      mutate({
        controlId,
        resultId: controlResultId,
        data: {
          ownerId,
        },
      });
    },
    [mutate, controlId],
  );

  const canUpdateOwnership = useHasCompanyPermission(
    CompanyPermission.ControlResultManageOwnership,
    user,
    companyId,
  );

  const canStartInvestigationAndReinclude = useHasCompanyPermission(
    CompanyPermission.ControlResultStartInvestigation,
    user,
    companyId,
  );

  const canSkipResult = useHasCompanyPermission(
    CompanyPermission.ControlResultSkip,
    user,
    companyId,
  );

  const canCompleteInvestigation = useHasCompanyPermission(
    CompanyPermission.ControlResultCompleteInvestigation,
    user,
    companyId,
  );
  const canUpdatePriority = useHasCompanyPermission(
    CompanyPermission.ControlResultChangePriority,
    user,
    companyId,
  );
  // Memoized function for filtering transitions based on permissions
  const filteredTransitions = useMemo(
    () =>
      filterWorkflowTransitions(workflow?.transitions, {
        canStartInvestigationAndReinclude,
        canSkipResult,
        canCompleteInvestigation,
      }),
    [
      workflow?.transitions,
      canStartInvestigationAndReinclude,
      canCompleteInvestigation,
      canSkipResult,
    ],
  );
  return (
    <div className="flex-col justify-between space-y-4">
      <div className="flex gap-4 items-start">
        <ResultTitle
          controlResultId={titleControlResultId}
          descriptorName={descriptorName}
        />
        {workflow?.currentStep ? (
          <WorkflowStepTag
            workflowStep={workflow.currentStep}
            className="mt-1"
          />
        ) : null}
        {rootCause && (
          <StatusTag
            status="other"
            label={
              rootCause.labelKeyName ?? rootCause.name ?? "unknown_conclusion"
            }
            className="mt-1"
          />
        )}
      </div>
      <div className="flex gap-2">
        {!isBulkActionInProgress &&
          filteredTransitions.map((transition) => (
            <WorkflowFormProvider
              key={transition.id}
              schema={transition}
              companyId={companyId}
            >
              <WorkflowTransition
                onSubmit={async (formData) =>
                  await handleTransitionSubmit(transition.id, formData)
                }
              />
            </WorkflowFormProvider>
          ))}
        <div className="flex items-center gap-1.5">
          <Typography trans="owner" />
          <OwnershipSelector
            companyId={companyId}
            value={owner?.id ?? null}
            initialValue={owner ?? null}
            onChange={(ownerId) => handleOwnershipChange(ownerId, resultId)}
            readonly={!canUpdateOwnership || isBulkActionInProgress}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Typography trans="priority" />
          <PrioritySelector
            value={priority?.id ?? null}
            initialValue={priority ? priority : null}
            onChange={(priorityId) => {
              handlePriorityChange(priorityId);
            }}
            readonly={!canUpdatePriority || isBulkActionInProgress}
          />
        </div>
      </div>
    </div>
  );
};
